import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BookOpen, Plus, Award, AlertTriangle, CheckCircle2, Calendar,
  Filter, FileDown, Trash2,
} from "lucide-react";
import BackButton from "../components/BackButton";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";

const CAT_OPTS = [
  { v: "ecm", l: "ECM" }, { v: "corso", l: "Corso" }, { v: "congresso", l: "Congresso" },
  { v: "master", l: "Master" }, { v: "workshop", l: "Workshop" }, { v: "webinar", l: "Webinar" },
];
const STATUS_OPTS = [
  { v: "pianificato", l: "Pianificato", color: "bg-slate-100 text-slate-700" },
  { v: "iscritto",    l: "Iscritto",    color: "bg-sky-100 text-sky-700" },
  { v: "frequentato", l: "Frequentato", color: "bg-amber-100 text-amber-700" },
  { v: "completato",  l: "Completato",  color: "bg-emerald-100 text-emerald-700" },
  { v: "annullato",   l: "Annullato",   color: "bg-red-100 text-red-700" },
];

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("it-IT"); } catch { return s; }
}
function fmtEur(n) {
  if (n == null) return "—";
  return "€ " + Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function NewItemDialog({ open, onOpenChange, defaultCategory, onCreated }) {
  const [form, setForm] = useState({
    category: defaultCategory || "ecm",
    title: "", provider: "", location: "", is_online: false,
    start_date: "", end_date: "", registration_deadline: "",
    status: "pianificato", ecm_credits: "", cost: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (defaultCategory) setForm((f) => ({ ...f, category: defaultCategory })); }, [defaultCategory]);

  async function submit() {
    if (!form.title.trim()) { toast.error("Titolo obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "" || payload[k] == null) delete payload[k]; });
      if (payload.ecm_credits != null) payload.ecm_credits = parseFloat(payload.ecm_credits);
      if (payload.cost != null) payload.cost = parseFloat(payload.cost);
      const created = await api.formazioneCreate(payload);
      toast.success("Voce creata");
      onCreated?.(created);
      onOpenChange(false);
      setForm({ category: defaultCategory || "ecm", title: "", provider: "", location: "", is_online: false,
                start_date: "", end_date: "", registration_deadline: "",
                status: "pianificato", ecm_credits: "", cost: "", notes: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="formazione-new-dialog">
        <DialogHeader><DialogTitle>Nuova voce formativa</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto">
          <div>
            <Label>Categoria</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="formazione-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CAT_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Titolo *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   data-testid="formazione-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Ente/Provider</Label>
              <Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} /></div>
            <div><Label>Luogo</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
          </div>
          <div className="flex items-center justify-between">
            <Label className="mb-0">Online</Label>
            <Switch checked={form.is_online} onCheckedChange={(v) => setForm({ ...form, is_online: v })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data inizio</Label>
              <Input type="date" value={form.start_date}
                     onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label>Data fine</Label>
              <Input type="date" value={form.end_date}
                     onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
          </div>
          <div><Label>Deadline iscrizione</Label>
            <Input type="date" value={form.registration_deadline}
                   onChange={(e) => setForm({ ...form, registration_deadline: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Costo (€)</Label>
              <Input type="number" step="0.01" value={form.cost}
                     onChange={(e) => setForm({ ...form, cost: e.target.value })} /></div>
          </div>
          {form.category === "ecm" && (
            <div><Label>Crediti ECM</Label>
              <Input type="number" step="0.5" value={form.ecm_credits}
                     onChange={(e) => setForm({ ...form, ecm_credits: e.target.value })}
                     data-testid="formazione-credits" /></div>
          )}
          <div><Label>Note</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="formazione-submit">
            {saving ? "…" : "Crea"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FormazioneList() {
  const loc = useLocation();
  const initialCategory = new URLSearchParams(loc.search).get("category") || "";
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState(initialCategory || "all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [dialogOpen, setDialogOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (filterCat !== "all") params.category = filterCat;
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterYear) params.year = parseInt(filterYear);
      const [it, ec] = await Promise.all([
        api.formazioneList(params),
        api.formazioneEcmStats({ year: parseInt(filterYear) || undefined }).catch(() => null),
      ]);
      setItems(it || []);
      setStats(ec);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [filterCat, filterStatus, filterYear]);
  useEffect(() => { setFilterCat(initialCategory || "all"); }, [initialCategory]);

  const upcoming = useMemo(() =>
    items.filter((i) => i.registration_deadline &&
                        new Date(i.registration_deadline) >= new Date() &&
                        (new Date(i.registration_deadline) - new Date()) <= 30 * 86400000),
    [items]);

  async function delItem(id) {
    if (!window.confirm("Eliminare questa voce?")) return;
    try {
      await api.formazioneDelete(id);
      toast.success("Eliminata");
      load();
    } catch { toast.error("Errore"); }
  }

  return (
    <div className="space-y-5" data-testid="formazione-page">
      <BackButton to="/" testid="formazione-back-btn" />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <BookOpen size={22} className="opacity-70" />
            <h1 className="font-display text-3xl sm:text-4xl font-semibold">Formazione medica</h1>
          </div>
          <p className="mt-1 text-sm opacity-70">Corsi, congressi, ECM · {items.length} voci</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="formazione-new-btn">
          <Plus size={14} className="mr-1" /> Nuova voce
        </Button>
      </div>

      {/* ECM counter */}
      {stats && (
        <div className="pxos-card p-5" data-testid="ecm-stats-card">
          <div className="flex items-center gap-2 mb-2">
            <Award size={18} className="opacity-70" />
            <h2 className="font-display text-lg font-semibold">
              Crediti ECM {stats.year}
            </h2>
          </div>
          <div className="flex items-baseline gap-2">
            <div className="text-3xl font-bold" data-testid="ecm-total">
              {stats.total_credits.toFixed(1)}
            </div>
            <div className="opacity-70">/ {stats.target}</div>
            <div className={`ml-auto text-sm ${stats.total_credits >= stats.target ? "text-emerald-700" : "text-amber-700"}`}>
              {stats.total_credits >= stats.target
                ? "Obiettivo raggiunto"
                : `Mancano ${stats.missing.toFixed(1)} crediti`}
            </div>
          </div>
          <Progress value={Math.min(100, stats.completion_percent)} className="mt-2" />
          {stats.upcoming_count > 0 && (
            <div className="text-xs mt-2 opacity-70">
              {stats.upcoming_count} eventi pianificati/iscritti per il {stats.year}
            </div>
          )}
        </div>
      )}

      {upcoming.length > 0 && (
        <div className="pxos-card p-4 border-amber-200 bg-amber-50" data-testid="formazione-upcoming">
          <div className="flex items-center gap-2 mb-2 text-amber-800">
            <AlertTriangle size={16} />
            <h3 className="font-semibold text-sm">Iscrizioni in scadenza (30 gg)</h3>
          </div>
          <ul className="space-y-1 text-sm text-amber-800">
            {upcoming.map((i) => (
              <li key={i.id}>
                <strong>{i.title}</strong> · deadline {fmtDate(i.registration_deadline)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Filters */}
      <div className="pxos-card p-3">
        <div className="flex flex-wrap items-center gap-3">
          <Filter size={14} className="opacity-60" />
          <div className="flex items-center gap-2">
            <Label className="text-xs">Categoria</Label>
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger className="w-40 h-9" data-testid="filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                {CAT_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Status</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {STATUS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Anno ECM</Label>
            <Input type="number" className="w-24 h-9" value={filterYear}
                   onChange={(e) => setFilterYear(e.target.value)} data-testid="filter-year" />
          </div>
        </div>
      </div>

      {/* List */}
      <div className="pxos-card p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center opacity-70">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center opacity-70" data-testid="formazione-empty">
            Nessuna voce con questi filtri.
          </div>
        ) : (
          <ul data-testid="formazione-list">
            {items.map((i) => {
              const stObj = STATUS_OPTS.find((s) => s.v === i.status);
              return (
                <li key={i.id}
                    className="p-4 border-b flex flex-wrap items-center justify-between gap-2"
                    style={{ borderColor: "var(--pxos-border)" }}
                    data-testid={`formazione-item-${i.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{i.title}</div>
                    <div className="text-xs opacity-70 flex flex-wrap items-center gap-2 mt-0.5">
                      <Badge variant="outline">{CAT_OPTS.find((c) => c.v === i.category)?.l || i.category}</Badge>
                      {i.provider && <span>{i.provider}</span>}
                      {i.start_date && <span>· <Calendar size={10} className="inline" /> {fmtDate(i.start_date)}</span>}
                      {i.ecm_credits && <span>· {i.ecm_credits} crediti</span>}
                      {i.cost && <span>· {fmtEur(i.cost)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${stObj?.color || ""}`}>
                      {stObj?.l || i.status}
                    </span>
                    <Button variant="ghost" size="icon" onClick={() => delItem(i.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <NewItemDialog open={dialogOpen} onOpenChange={setDialogOpen}
                     defaultCategory={filterCat !== "all" ? filterCat : "ecm"}
                     onCreated={() => load()} />
    </div>
  );
}
